import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import type { EcoNode } from '../App';
import { useI18n } from '../i18n';
import {
  descriptionWithoutCaps,
  fetchOracleTools,
  parseCapsFromDescription,
  slugFromNodeId,
  type OracleManifest,
} from '../lib/oracleManifest';
import MomusCard from './cards/MomusCard';
import NodeVisual, { hasNodeVisual } from './cards/NodeVisual';
import TreasuryCard from './cards/TreasuryCard';
import MetisCard from './cards/MetisCard';
import BridgesCard from './cards/BridgesCard';
import FactoryAgentsCard from './cards/FactoryAgentsCard';
import GaiaCard from './cards/GaiaCard';
import AtlasCard from './cards/AtlasCard';
import DioscuriCard from './cards/DioscuriCard';
import SkoposCard from './cards/SkoposCard';
import HeliosCard from './cards/HeliosCard';
import LogosCard from './cards/LogosCard';
import UseCasesCard from './cards/UseCasesCard';
import SignalHuntCard from './cards/SignalHuntCard';
import ThemisCard from './cards/ThemisCard';
import BasanosCard from './cards/BasanosCard';
import HestiaCard from './cards/HestiaCard';
import HistorCard from './cards/HistorCard';
import DolosCard from './cards/DolosCard';
import EsimOperatorCard, { ESIM_CARD_METRICS } from './cards/EsimOperatorCard';
import NodeSceneSlot from '../nodeScenes/NodeSceneSlot';
import { resolveNodeScene } from '../nodeScenes/resolve';

interface Props {
  node: EcoNode;
  onClose: () => void;
  themeColor: string;
  mobile?: boolean;
  /** Open the built-in chain scanner on one address (a declared escrow or wallet). */
  onScanAddress?: (address: string) => void;
}

/** Compact explorer / hex URL for the card footer so it cannot overflow the panel. */
export function truncateDisplayUrl(url: string, max = 36): string {
  const raw = url.trim();
  if (raw.length <= max) return raw;
  try {
    const u = new URL(raw);
    const hex = u.pathname.match(/0x[a-fA-F0-9]{12,}/i)?.[0];
    if (hex) {
      const shortHex = `${hex.slice(0, 8)}…${hex.slice(-5)}`;
      const shown = `${u.host}${u.pathname.replace(hex, shortHex)}`;
      if (shown.length <= max + 8) return shown;
    }
    const rest = `${u.host}${u.pathname}`;
    if (rest.length <= max) return rest;
    return `${rest.slice(0, Math.max(12, max - 10))}…${rest.slice(-8)}`;
  } catch {
    return `${raw.slice(0, Math.max(12, max - 10))}…${raw.slice(-8)}`;
  }
}

function NodeCtaLink({
  href,
  label,
  themeColor,
}: {
  href: string;
  label: string;
  themeColor: string;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const rawUrl = label.trim() === href.trim();
  const long = rawUrl && href.length > 36;
  const display = long && !hovered ? truncateDisplayUrl(href) : label;

  const copy = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard can be denied; the full URL is still on the link */
    }
  };

  return (
    <div
      className="flex items-stretch gap-1 min-w-0"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        title={long ? href : undefined}
        onClick={(e) => e.stopPropagation()}
        className={`min-w-0 flex-1 text-[11px] font-mono font-bold text-center rounded-lg py-2.5 px-2 ${
          long && hovered ? 'break-all whitespace-normal' : 'truncate'
        }`}
        style={{ backgroundColor: themeColor + '1f', border: `1px solid ${themeColor}55`, color: themeColor }}
      >
        {long ? display : `${label} ↗`}
      </a>
      {long ? (
        <button
          type="button"
          onClick={copy}
          title={t('nodeDetail.copyUrl', undefined, 'Copy')}
          className="shrink-0 px-2 rounded-lg text-[10px] font-mono font-bold"
          style={{ backgroundColor: themeColor + '1f', border: `1px solid ${themeColor}55`, color: themeColor }}
        >
          {copied ? t('nodeDetail.copied', undefined, 'Copied') : t('nodeDetail.copyUrl', undefined, 'Copy')}
        </button>
      ) : null}
    </div>
  );
}

function isExpandableMetric(value: unknown): boolean {
  const s = String(value);
  return s.length > 16 || /^0x[a-fA-F0-9]{10,}$/.test(s);
}

function truncateMetric(value: unknown): string {
  const s = String(value);
  if (s.length <= 18) return s;
  if (/^0x[a-fA-F0-9]+$/.test(s) && s.length > 14) {
    return `${s.slice(0, 8)}…${s.slice(-6)}`;
  }
  return `${s.slice(0, 14)}…`;
}

function MetricCell({
  metricKey,
  metricLabel,
  value,
  themeColor,
  expanded,
  onToggle,
}: {
  metricKey: string;
  metricLabel: string;
  value: unknown;
  themeColor: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const full = typeof value === 'number' ? value.toLocaleString() : String(value);
  const expandable = isExpandableMetric(value);
  const display = expanded || !expandable ? full : truncateMetric(value);

  return (
    <div
      role={expandable ? 'button' : undefined}
      tabIndex={expandable ? 0 : undefined}
      onClick={expandable ? onToggle : undefined}
      onKeyDown={
        expandable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle();
              }
            }
          : undefined
      }
      className={`relative px-3 py-2 rounded transition-all duration-300 ease-out ${
        expandable ? 'cursor-pointer hover:brightness-110' : ''
      } ${expanded ? 'col-span-2 z-40' : 'min-w-0'}`}
      style={{
        backgroundColor: themeColor + (expanded ? '18' : '0a'),
        border: `1px solid ${themeColor}${expanded ? '66' : '18'}`,
        transform: expanded
          ? 'perspective(720px) translateZ(28px) scale(1.06) rotateX(6deg)'
          : undefined,
        transformStyle: 'preserve-3d',
        boxShadow: expanded ? `0 12px 32px rgba(0,0,0,0.55), 0 0 20px ${themeColor}33` : undefined,
      }}
    >
      <div
        className={`font-mono font-bold ${expanded ? 'text-base break-all leading-snug' : 'text-sm truncate'}`}
        style={{ color: themeColor }}
        title={expandable && !expanded ? full : undefined}
      >
        {display}
      </div>
      <div className="text-[10px] font-mono text-white/40 mt-0.5">
        {metricLabel}
      </div>
    </div>
  );
}

export default function NodeDetail({ node, onClose, themeColor, mobile = false, onScanAddress }: Props) {
  const { t } = useI18n();
  const panelRef = useRef<HTMLDivElement>(null);
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);
  const [expandedChild, setExpandedChild] = useState<string | null>(null);
  const [sensorQ, setSensorQ] = useState('');

  // A node with its own card shows these numbers there, labelled and explained; repeating them
  // here as bare keys ("offers", "buyers 30d") only made the panel twice as long.
  const visibleMetrics = Object.entries(node.metrics || {}).filter(
    ([key]) => !(node.group === 'esim_operator' && ESIM_CARD_METRICS.has(key)),
  );

  const metricLabel = (key: string) =>
    t(`nodeDetail.metricKeys.${key}`, undefined, key.replace(/_/g, ' '));

  useEffect(() => {
    setSensorQ('');
  }, [node.id]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (expandedChild) setExpandedChild(null);
        else if (expandedMetric) setExpandedMetric(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, expandedMetric, expandedChild]);

  const toggleMetric = (key: string) => {
    setExpandedMetric((prev) => (prev === key ? null : key));
  };

  const statusColor =
    node.status === 'active' ? '#00ff88' :
    node.status === 'error' ? '#ff3355' :
    node.status === 'idle' ? '#ffdd00' : '#666666';

  useEffect(() => {
    if (!mobile) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobile]);

  // --- Unified 3D / visual preview (oracles + momus/metis/atlas + competing galaxy) ---
  const isOracle = node.group === 'oracle' && node.id.startsWith('oracle-');
  const oracleSlug = isOracle ? slugFromNodeId(node.id) : '';
  const nodeScene = useMemo(() => resolveNodeScene(node), [node]);

  // Capability ids parsed off the node — the always-available fallback.
  const fallbackCaps = useMemo(
    () => (isOracle ? parseCapsFromDescription(node.description) : []),
    [isOracle, node.description],
  );
  const oracleBlurb = useMemo(
    () => (isOracle ? descriptionWithoutCaps(node.description) : ''),
    [isOracle, node.description],
  );

  // Live products & services from the oracle's AI-Market manifest (best-effort, cached).
  const [manifest, setManifest] = useState<OracleManifest | null>(null);
  useEffect(() => {
    if (!isOracle) {
      setManifest(null);
      return undefined;
    }
    let alive = true;
    setManifest(null);
    fetchOracleTools(node.url, oracleSlug)
      .then((m) => {
        if (alive) setManifest(m);
      })
      .catch(() => {
        if (alive) setManifest(null);
      });
    return () => {
      alive = false;
    };
  }, [isOracle, node.url, oracleSlug]);
  const manifestTools = manifest?.tools ?? null;

  return (
    <>
      {mobile && (
        <button
          type="button"
          className="mobile-backdrop"
          aria-label={t('mobile.closeSheet')}
          onClick={onClose}
        />
      )}
      <div
      ref={panelRef}
      className={`z-40 glass-panel p-4 md:p-5 animate-slide-in ${
        mobile
          ? 'fixed inset-x-0 mobile-sheet flex flex-col overflow-hidden'
          : 'absolute left-4 top-24 w-80 max-h-[calc(100vh-8rem)] overflow-y-auto'
      }`}
      style={{
        borderColor: themeColor + '44',
        boxShadow: `0 0 30px rgba(0,0,0,0.5), 0 0 15px ${themeColor}22`,
        perspective: '900px',
      }}
      onClick={() => {
        if (expandedMetric) setExpandedMetric(null);
        if (expandedChild) setExpandedChild(null);
      }}
    >
      <div className={mobile ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain' : undefined}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4" onClick={(e) => e.stopPropagation()}>
        {/* `min-w-0` is load-bearing: a flex child will not shrink below its content, so the
            title could never wrap and instead stretched the whole panel sideways — visible on
            any peer with a descriptive name ("CHARON — liquidation risk for the side that
            borrows", "ATLAS — planetary sensor map & composite briefs").

            It WRAPS, and it is not clamped. A two-line clamp was here and looked like the fix
            until a narrow phone showed what it actually does: that title needs three lines at
            ~420px, the third was cut, and the result reads as a name that simply stops
            mid-word — the exact thing the clamp was added to prevent. A hub's name is the one
            string in this panel worth reading in full, the panel already scrolls vertically,
            and a name is a few words, not a paragraph. So let it take the lines it needs. */}
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <div
            className="w-3 h-3 rounded-full mt-1 shrink-0"
            style={{ backgroundColor: statusColor, boxShadow: `0 0 6px ${statusColor}` }}
          />
          <h3
            className="text-sm font-semibold min-w-0 leading-snug"
            style={{
              color: themeColor,
              // `anywhere` and not `break-word`: a peer may advertise itself as one long
              // unbroken token, and that must fold inside the panel rather than widen it.
              overflowWrap: 'anywhere',
            }}
            title={node.label}
          >
            {node.label}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="text-white/40 hover:text-white/80 transition-colors text-2xl leading-none w-10 h-10 flex items-center justify-center shrink-0"
          aria-label={t('mobile.closeSheet')}
        >
          ×
        </button>
      </div>

      {/* Description — localized per node id, with the backend English text as fallback. */}
      <p className="text-xs text-white/60 mb-4 leading-relaxed">
        {t(`nodeDetail.desc.${node.id}`, undefined, isOracle ? oracleBlurb : node.description)}
      </p>

      {/* Unified scene slot — oracles, Momus/Metis/Atlas, use_cases, signal_hunt, … */}
      {nodeScene && (
        <NodeSceneSlot
          scene={nodeScene}
          node={node}
          themeColor={themeColor}
          mobile={mobile}
          t={t}
        />
      )}
      {/* Legacy shim path unused when nodeScene resolves; keep hasNodeVisual for tests. */}
      {!nodeScene && hasNodeVisual(node) && (
        <NodeVisual node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}

      {/* Group badge */}
      <div className="mb-4">
        <span
          className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase"
          style={{
            backgroundColor: themeColor + '18',
            color: themeColor,
            border: `1px solid ${themeColor}44`,
          }}
        >
          {t(`group.${node.group}`, undefined, node.group)}
        </span>
        <span
          className="inline-block ml-2 px-2 py-0.5 rounded text-[10px] font-mono uppercase"
          style={{
            backgroundColor: statusColor + '18',
            color: statusColor,
            border: `1px solid ${statusColor}44`,
          }}
        >
          {t(`status.${node.status}`)}
        </span>
        {/* A fixture says so, next to its own status. Without this the row reads
            "NETWORK · ACTIVE" for a hub that is a name from a list. */}
        {node.simulated && (
          <span
            className="inline-block ml-2 px-2 py-0.5 rounded text-[10px] font-mono uppercase"
            style={{
              backgroundColor: '#ffcc3318',
              color: '#ffcc33',
              border: '1px solid #ffcc3344',
            }}
            data-testid="node-simulated-badge"
          >
            {t('nodeDetail.simulated', undefined, 'simulated')}
          </span>
        )}
        {node.simulated && (
          <div
            className="mt-1.5 text-[10px] leading-snug text-white/45"
            data-testid="node-simulated-note"
          >
            {t(
              'nodeDetail.simulatedNote',
              undefined,
              node.simulated_note || 'a fixture of the UNI scenario — nothing answers for it',
            )}
          </div>
        )}
      </div>

      {/* Hub affiliation — every node belongs to a federation Hub */}
      {node.hub && (
        <div className="mb-4" onClick={(e) => e.stopPropagation()}>
          <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-1.5">
            {t('nodeDetail.hub')}
          </div>
          <div
            className="px-3 py-2 rounded"
            style={{
              backgroundColor: themeColor + '0a',
              border: `1px solid ${themeColor}28`,
            }}
          >
            {node.hub.url ? (
              <a
                href={node.hub.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold hover:underline"
                style={{ color: themeColor }}
              >
                {node.hub.label}
              </a>
            ) : (
              <span className="text-xs font-semibold" style={{ color: themeColor }}>
                {node.hub.label}
              </span>
            )}
            {node.hub.id === node.id && (
              <div className="text-[10px] font-mono text-white/35 mt-1">
                {t('nodeDetail.hubSelf')}
              </div>
            )}
            {node.hub.url && node.hub.id !== node.id && (
              <div className="text-[10px] font-mono text-white/35 mt-1 truncate" title={node.hub.url}>
                {node.hub.url.replace(/^https?:\/\//, '')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* On chain — what this node declared, and what the chain said about it.
          The declaration comes from its hub's signed well-known; the verdict comes from
          this monitor asking the chain itself. Both are shown, because they are different
          claims: an unverified address is a string somebody published, and rendering it
          the same way as a confirmed contract is how a map starts lying politely. */}
      {node.contracts && node.contracts.entries.length > 0 && (
        <div className="mb-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <div className="text-[10px] font-mono uppercase tracking-wider text-white/40">
              {t('nodeDetail.onchain.title', undefined, 'ON CHAIN')}
            </div>
            <div className="text-[10px] font-mono text-white/35 truncate" title={node.contracts.network || ''}>
              {node.contracts.network || node.contracts.chain || ''}
              {typeof node.contracts.chain_id === 'number' ? ` · ${node.contracts.chain_id}` : ''}
              {node.contracts.simulated
                ? ` · ${t('nodeDetail.onchain.simulated', undefined, 'simulated')}`
                : node.contracts.testnet
                  ? ' · testnet'
                  : ''}
            </div>
          </div>
          <div className="space-y-1.5">
            {node.contracts.entries.map((entry) => (
              <div
                key={entry.address}
                className="px-3 py-2 rounded"
                style={{
                  backgroundColor: themeColor + '0a',
                  border: `1px solid ${entry.verified ? themeColor + '38' : '#ffffff14'}`,
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] font-semibold uppercase tracking-wide" style={{ color: entry.verified ? themeColor : 'rgba(255,255,255,0.45)' }}>
                    {entry.role}
                  </span>
                  <span
                    className="font-mono text-[10px] shrink-0"
                    style={{ color: entry.verified ? themeColor : 'rgba(255,255,255,0.35)' }}
                    title={entry.reason || ''}
                  >
                    {entry.verified
                      ? t('nodeDetail.onchain.verified', undefined, 'verified on chain')
                      : t('nodeDetail.onchain.unverified', undefined, 'declared, not verified')}
                  </span>
                </div>
                <div className="font-mono text-[10px] text-white/50 break-all mt-0.5" title={entry.address}>
                  {entry.address}
                </div>
                {entry.reason && (
                  <div className="text-[10px] font-mono text-white/35 mt-0.5">{entry.reason}</div>
                )}
                <div className="flex items-center gap-3 mt-1">
                  {/* This monitor's own scanner: works on the bubble too, where no public
                      explorer exists at all. */}
                  {onScanAddress && (
                    <button
                      type="button"
                      onClick={() => onScanAddress(entry.address)}
                      className="font-mono text-[10px] hover:underline"
                      style={{ color: themeColor }}
                    >
                      {t('nodeDetail.onchain.viewTxs', undefined, 'view transactions →')}
                    </button>
                  )}
                  {entry.explorer && (
                    <a
                      href={entry.explorer}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-[10px] text-white/45 hover:underline"
                    >
                      {t('nodeDetail.onchain.explorer', undefined, 'explorer ↗')}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Oracle products & services — capabilities (id · what · price) + math one-liner.
          Prefers the live AI-Market manifest; falls back to capability ids on the node. */}
      {isOracle && (manifestTools?.length || fallbackCaps.length > 0) && (
        <div className="mb-4" onClick={(e) => e.stopPropagation()}>
          <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-2">
            {t('nodeDetail.oracle.products')}
          </div>
          <div className="space-y-1.5">
            {manifestTools && manifestTools.length > 0
              ? manifestTools.map((tool) => (
                  <div
                    key={tool.capability_id}
                    className="px-3 py-2 rounded"
                    style={{
                      backgroundColor: themeColor + '0a',
                      border: `1px solid ${themeColor}18`,
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className="font-mono text-xs font-semibold truncate"
                        style={{ color: themeColor }}
                        title={tool.capability_id}
                      >
                        {tool.capability_id}
                      </span>
                      {typeof tool.price_per_call_usd === 'number' && (
                        <span className="font-mono text-[10px] text-white/50 shrink-0">
                          ${tool.price_per_call_usd}
                        </span>
                      )}
                    </div>
                    {tool.description && (
                      <div className="text-[11px] text-white/50 mt-0.5 leading-snug">
                        {tool.description}
                      </div>
                    )}
                  </div>
                ))
              : fallbackCaps.map((cap) => (
                  <div
                    key={cap}
                    className="px-3 py-1.5 rounded font-mono text-xs"
                    style={{
                      backgroundColor: themeColor + '0a',
                      border: `1px solid ${themeColor}18`,
                      color: themeColor,
                    }}
                  >
                    {cap}
                  </div>
                ))}
          </div>
          {/* Math one-liner — oracle-level description from the manifest when available. */}
          {manifest?.description && (
            <p className="mt-2 text-[11px] text-white/45 leading-snug">
              <span className="font-mono uppercase tracking-wider text-white/30">
                {t('nodeDetail.oracle.math')}:
              </span>{' '}
              {manifest.description}
            </p>
          )}
        </div>
      )}

      {/* Every seller running the eSIM operator kit, discovered through the hub — by group,
          since each operator has its own id. Right under its scene: this IS the node. */}
      {node.group === 'esim_operator' && (
        <EsimOperatorCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}

      {/* Metrics */}
      {visibleMetrics.length > 0 && (
        <div className="mb-4" onClick={(e) => e.stopPropagation()}>
          <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-2">
            {t('nodeDetail.metrics')}
          </div>
          <div className="grid grid-cols-2 gap-2 min-w-0">
            {visibleMetrics.map(([key, value]) => (
              <MetricCell
                key={key}
                metricKey={key}
                metricLabel={metricLabel(key)}
                value={value}
                themeColor={themeColor}
                expanded={expandedMetric === key}
                onToggle={() => toggleMetric(key)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Sub-components — generic nodes, or DIOSCURI twins + collaboration split.
          DIOSCURI renders its own card (twins/collaboration + community links). */}
      {node.id === 'dioscuri' ? (
        <DioscuriCard
          node={node}
          themeColor={themeColor}
          mobile={mobile}
          t={t}
          expandedChild={expandedChild}
          setExpandedChild={setExpandedChild}
        />
      ) : node.children && node.children.length > 0 ? (
        <div onClick={(e) => e.stopPropagation()}>
          <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-2">
            {t('nodeDetail.subcomponents', { count: node.children.length })}
          </div>
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {node.children.map((child) => {
              const childBody = (
                <>
                  <div
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: themeColor, opacity: 0.6 }}
                  />
                  <span className="text-white/70">{child.label}</span>
                  {child.url && <span className="ml-auto text-white/30">↗</span>}
                </>
              );
              return child.url ? (
                <a
                  key={child.id}
                  href={child.url}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded text-xs flex items-center gap-2 transition-colors hover:brightness-110"
                  style={{ backgroundColor: themeColor + '08', color: themeColor }}
                >
                  {childBody}
                </a>
              ) : (
                <div
                  key={child.id}
                  className="px-3 py-1.5 rounded text-xs flex items-center gap-2"
                  style={{ backgroundColor: themeColor + '08' }}
                >
                  {childBody}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* HELIOS YouTube channel link */}
      {node.id === 'helios' && node.youtube_url && (
        <HeliosCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}

      {/* SKOPOS — dashboard + docs links (public status in metrics). */}
      {node.id === 'skopos' && node.links && Object.keys(node.links).length > 0 && (
        <SkoposCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}
      {node.id === 'gaia' && (
        <GaiaCard
          node={node}
          themeColor={themeColor}
          mobile={mobile}
          sensorQ={sensorQ}
          setSensorQ={setSensorQ}
          t={t}
        />
      )}
      {node.id === 'atlas' && (
        <AtlasCard
          node={node}
          themeColor={themeColor}
          mobile={mobile}
          sensorQ={sensorQ}
          setSensorQ={setSensorQ}
          t={t}
        />
      )}
      {node.id === 'metis' && (
        <MetisCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}
      {node.id === 'momus' && (
        <MomusCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}
      {node.id === 'use_cases' && (
        <UseCasesCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}
      {node.id === 'signal_hunt' && (
        <SignalHuntCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}
      {/* TREASURY — the separate payer. Ledger tail + the separation, from the other side. */}
      {node.id === 'treasury' && node.treasury_live && (
        <TreasuryCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}

      {/* BRIDGES — the third paid invoke channel, and the only one nothing counts. */}
      {node.id === 'logos' && (
        <LogosCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}
      {node.id === 'bridges' && node.bridges_live && (
        <BridgesCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}
      {node.id === 'themis' && (
        <ThemisCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}

      {node.id === 'basanos' && (
        <BasanosCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}
      {node.id === 'hestia' && (
        <HestiaCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}
      {node.id === 'histor' && (
        <HistorCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}
      {node.id === 'dolos' && (
        <DolosCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}

      {/* Agents the factory shipped — who is running, on which SDK, and what they spend. */}
      {node.id === 'factory_agents' && (
        <FactoryAgentsCard node={node} themeColor={themeColor} mobile={mobile} t={t} />
      )}
      </div>

      {/* Landing / live / node URL — pinned on mobile so the dock cannot cover the exit. */}
      {(() => {
        // A fixture has no addresses at all. Its own flag decides that here, rather than
        // trusting every producer to have withheld the fields: whatever a simulated node
        // carries, this panel does not print it as somewhere a service answers.
        if (node.simulated) return null;
        const items: { href: string; label: string }[] = [];
        const seen = new Set<string>();
        const add = (href: string | undefined, label: string) => {
          const url = (href || '').trim();
          if (!url || seen.has(url)) return;
          seen.add(url);
          items.push({ href: url, label });
        };
        add(node.links?.landing, t('nodeDetail.openLanding', undefined, 'Open landing →'));
        add(node.links?.live, t('nodeDetail.openLive', undefined, 'Open live →'));
        add(node.url, node.url || '');
        // An address that only resolves inside its own operator's network is shown, but never
        // as a link: a foreign hub declares its parts by compose hostname
        // (`http://provenance-ledger:8812`), and a card that offers that as somewhere to click
        // teaches the reader that its links are worthless.
        const internal = (node.url_internal || '').trim();
        // Where these numbers were actually read from. On a host that runs both realms the
        // polled address and the operator-facing link are different services, and which one
        // a figure came from is the difference between "the bubble's mesh" and "the live
        // one" — so the card states it instead of leaving it to the deploy environment.
        const source = (node.source_url || '').trim();
        if (items.length === 0 && !internal && !source) return null;
        return (
          <div className={`pt-3 border-t shrink-0 space-y-1.5 overflow-x-hidden ${mobile ? 'mt-3' : 'mt-4'}`} style={{ borderColor: themeColor + '22' }}>
            {items.map((item) => (
              <NodeCtaLink key={item.href} href={item.href} label={item.label} themeColor={themeColor} />
            ))}
            {internal && (
              <div
                className="rounded border px-2.5 py-1.5 font-mono text-[10px] leading-snug break-all"
                style={{ borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.45)' }}
                data-testid="node-internal-address"
                title={internal}
              >
                <span className="mr-1.5 uppercase tracking-wider" style={{ color: '#ffcc33' }}>
                  {t('nodeDetail.internalAddress', undefined, 'internal address')}
                </span>
                {internal}
              </div>
            )}
            {source && (
              <div
                className="rounded border px-2.5 py-1.5 font-mono text-[10px] leading-snug break-all"
                style={{ borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.45)' }}
                data-testid="node-source-address"
                title={source}
              >
                <span className="mr-1.5 uppercase tracking-wider" style={{ color: themeColor }}>
                  {t('nodeDetail.polledFrom', undefined, 'polled from')}
                </span>
                {source}
                {node.source_shared && (
                  <span className="ml-1.5 uppercase tracking-wider" style={{ color: '#ffcc33' }}>
                    · {t('nodeDetail.sharedWithHost', undefined, 'shared with the host')}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })()}
    </div>
    </>
  );
}
